import { Category, CatalogPromotionDto } from '../types';
import { formatPrice } from './restaurantUtils';

export const PROMOTION_TYPE_LABELS: Record<CatalogPromotionDto['type'], string> = {
  COMBO_FIXED_PRICE: 'Combo com preço fixo',
  QUANTITY_PERCENTAGE_DISCOUNT: 'Desconto percentual por quantidade',
  QUANTITY_FIXED_DISCOUNT: 'Desconto fixo por quantidade',
  BUY_X_GET_Y_PERCENTAGE: 'Leve X e ganhe desconto',
  BUY_X_GET_Y_FIXED: 'Leve X e ganhe desconto fixo',
  BUY_X_GET_Y_FREE: 'Leve X e ganhe grátis',
};

export const PROMOTION_DISCOUNT_LABELS: Record<string, string> = {
  PERCENTAGE: 'percentual',
  FIXED_AMOUNT: 'valor fixo',
  FREE: 'grátis',
  FIXED_PRICE: 'preço fixo',
};

const formatDateLabel = (date?: string) => {
  if (!date) return null;
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).format(parsedDate);
};

export const getPromotionTypeLabel = (type?: CatalogPromotionDto['type']) => {
  if (!type) return 'Promoção';
  return PROMOTION_TYPE_LABELS[type] || type;
};

export const getPromotionDateRange = (promotion: CatalogPromotionDto) => {
  const startDate = formatDateLabel(promotion.startDate);
  const endDate = formatDateLabel(promotion.endDate);

  if (startDate && endDate) {
    return `${startDate} a ${endDate}`;
  }

  return startDate || endDate || null;
};

export const getPromotionConditionSummary = (promotion: CatalogPromotionDto) => {
  if (!promotion.conditions || promotion.conditions.length === 0) return null;

  return promotion.conditions
    .map(condition => `${condition.requiredQuantity}x ${condition.targetType === 'CATEGORY' ? 'categoria' : 'produto'}`)
    .join(' + ');
};

export const getPromotionBenefitSummary = (promotion: CatalogPromotionDto) => {
  if (!promotion.benefits || promotion.benefits.length === 0) return null;

  return promotion.benefits
    .map(benefit => {
      const target = benefit.targetType === 'CATEGORY' ? 'categoria' : 'produto';
      const discount = PROMOTION_DISCOUNT_LABELS[benefit.discountType] || benefit.discountType;
      const value = benefit.discountType === 'FREE'
        ? 'grátis'
        : benefit.discountType === 'FIXED_PRICE' && typeof benefit.fixedComboPrice === 'number'
          ? formatPrice(benefit.fixedComboPrice)
          : benefit.discountType === 'PERCENTAGE'
            ? `${benefit.discountValue}%`
            : formatPrice(benefit.discountValue);

      return `${benefit.benefitQuantity}x ${target} com ${discount} (${value})`;
    })
    .join(' + ');
};

export const getPromotionSummary = (promotion: CatalogPromotionDto) => {
  const parts = [
    getPromotionDateRange(promotion),
    typeof promotion.minimumOrderValue === 'number' ? `Pedido mínimo ${formatPrice(promotion.minimumOrderValue)}` : null,
    typeof promotion.maximumDiscountValue === 'number' ? `Desconto máx. ${formatPrice(promotion.maximumDiscountValue)}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' • ') : 'Promoção ativa no cardápio';
};

export const collectVisiblePromotions = (categories: Category[]) => {
  const seen = new Map<string, CatalogPromotionDto>();

  categories.forEach(category => {
    (category.promos || []).forEach(promotion => {
      if (!promotion.active || promotion.showInMenu === false) return;
      if (!seen.has(promotion.id)) {
        seen.set(promotion.id, promotion);
      }
    });
  });

  return Array.from(seen.values()).sort((a, b) => {
    const priorityA = a.priority ?? 999999;
    const priorityB = b.priority ?? 999999;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.name.localeCompare(b.name);
  });
};
